/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { settingsApi } from '../../api';

import { useLoader } from '../../components/common';
import { SettingsPage, Panel, Choice } from './shell';

/* The original stamps a build id at the top of this page (`Version :
   6.26.3-260727-1`). Ours is the package version injected at build time, so the
   string identifies a build rather than being assembled from the current date —
   which would drift every day without anything actually having been rebuilt. */
const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';

const WORK_INFO_OPTIONS = [
  ['showRoundsOnBoard', 'แสดงจำนวนรอบในหน้าแรก'],
  ['showHoursOnBoard', 'แสดงจำนวนชั่วโมงในหน้าแรก'],
];

/* The bands the booking cards are painted with, listed under
   กำหนดสีแถบการ์ดการจอง so the choice above has something to refer to. */
const CARD_COLOR_LEGEND = [
  { label: 'จอง / กำลังนวด', swatches: ['var(--purple-600)', 'var(--green-600)'] },
  { label: 'ยังไม่มา / เลยเวลาจอง', swatches: ['var(--ink-300)', 'var(--red-600)'] },
];

export function SettingGeneral() {
  const general = useLoader(() => settingsApi.general(), []);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  /* กำหนดสีแถบการ์ดการจอง has no key behind it: updateGeneralSettings whitelists
     the fields it writes and there is no branch_setting row for this one, so a
     value put in `form` would be dropped on the way through. Held apart from the
     save payload instead — the control works for the session but never pretends
     the choice was persisted. */
  const [cardColorBy, setCardColorBy] = useState('status');

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.updateGeneral(form);
      general.reload();
      setForm(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPage>
      <Panel loader={general}>
        {(d) => {
          // The เซอร์วิสชาร์จ percentage is a branch column, not one of the
          // key/value settings, so the baseline has to be assembled from both.
          const base = {
            ...d.settings,
            serviceChargeRate: d.branch?.service_charge_rate ?? 0,
          };
          const v = form || base;
          const set = (k) => (val) => setForm({ ...v, [k]: val });

          const preview = [
            v.showRoundsOnBoard && '3 รอบ',
            v.showHoursOnBoard && '4:30 ชม.',
          ].filter(Boolean);

          return (
            /* Their panel is a flat white sheet the full width of the column —
               no border, no shadow, no max-width. See .setting-card. */
            <div className="setting-card">
              <div className="version">Version : {APP_VERSION}</div>

              <Choice
                label="ตั้งค่าการจ่ายค่าทิปให้กับหมอนวด"
                value={v.tipDeductsCashier}
                onChange={set('tipDeductsCashier')}
                options={[
                  { value: true, label: 'หักเงินจากแคชเชียร์' },
                  { value: false, label: 'ไม่หักเงินจากแคชเชียร์' },
                ]}
              />

              <div className="choice">
                <div className="choice-label">เซอร์วิสชาร์จ</div>
                <div className="choice-opts">
                  <label className={`radio${v.serviceChargeEnabled ? ' on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={!!v.serviceChargeEnabled}
                      onChange={(e) => setForm({ ...v, serviceChargeEnabled: e.target.checked })}
                    />
                    เรียกเก็บค่าเซอร์วิสชาร์จ
                  </label>
                  <span className="pct-field">
                    <input
                      className="pct"
                      type="number"
                      min="0"
                      max="100"
                      value={v.serviceChargeRate ?? 0}
                      onChange={(e) => setForm({ ...v, serviceChargeRate: e.target.value })}
                    />
                    <span className="pct-unit">%</span>
                  </span>
                </div>
              </div>

              <Choice
                label="การแสดงผลตารางหมอนวด"
                value={v.rosterOrder}
                onChange={set('rosterOrder')}
                options={[
                  { value: 'latest_first', label: 'สลับคิวหมอ คิวล่าสุดอยู่บนสุดเสมอ' },
                  { value: 'queue', label: 'ไม่สลับคิวหมอ ยึดลำดับคิวเริ่มงาน' },
                ]}
              />

              <div className="choice">
                <div className="choice-label">การแสดงข้อมูลการทำงานหมอนวด</div>
                <div className="choice-opts col">
                  {WORK_INFO_OPTIONS.map(([key, label]) => (
                    <label key={key} className={`radio${v[key] ? ' on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={!!v[key]}
                        onChange={(e) => setForm({ ...v, [key]: e.target.checked })}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="choice-preview">
                  <div className="choice-preview-label">ตัวอย่างการแสดงผล</div>
                  <div className="therapist-preview">
                    <span className="staff-avatar" />
                    <span className="therapist-preview-name">A01 (แอน)</span>
                    <span className="therapist-preview-meta">
                      {preview.length
                        ? preview.map((p) => <em key={p}>{p}</em>)
                        : <em className="off">ไม่แสดงข้อมูลการทำงาน</em>}
                    </span>
                  </div>
                </div>
              </div>

              <div className="choice">
                <div className="choice-label">กำหนดสีแถบการ์ดการจอง</div>
                <div className="choice-opts">
                  {[
                    { value: 'status', label: 'สถานะบริการ' },
                    { value: 'service', label: 'บริการ' },
                  ].map((o) => (
                    <label
                      key={o.value}
                      className={`radio${cardColorBy === o.value ? ' on' : ''}`}
                    >
                      <input
                        type="radio"
                        checked={cardColorBy === o.value}
                        onChange={() => setCardColorBy(o.value)}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
                <div className="card-legend">
                  {CARD_COLOR_LEGEND.map((row) => (
                    <span className="legend-item" key={row.label}>
                      <span className="legend-swatches">
                        {row.swatches.map((c) => (
                          <i className="legend-swatch" key={c} style={{ background: c }} />
                        ))}
                      </span>
                      {row.label}
                    </span>
                  ))}
                </div>
              </div>

              <Choice
                label="เวลาชำระเงินเริ่มต้น"
                value={v.paymentTimeMode}
                onChange={set('paymentTimeMode')}
                options={[
                  { value: 'auto', label: 'ปรับตามเวลาจองอัตโนมัติ (ใช้เวลาชำระจริงก่อนเวลาจอง)' },
                  { value: 'actual', label: 'ตามเวลาชำระเงินจริง' },
                ]}
              />

              <Choice
                label="แสดงค่ามือในหน้าชำระเงิน"
                value={v.showCommissionOnPayment}
                onChange={set('showCommissionOnPayment')}
                options={[{ value: false, label: 'ไม่แสดง' }, { value: true, label: 'แสดง' }]}
              />

              <Choice
                label="สิทธิ์ขั้นต่ำในการแก้ไขค่ามือหมอ"
                value={v.minRoleEditCommission}
                onChange={set('minRoleEditCommission')}
                options={[
                  { value: 'owner', label: 'เจ้าของ' },
                  { value: 'manager', label: 'ผู้จัดการ' },
                  { value: 'staff', label: 'พนักงานทั่วไป' },
                ]}
              />

              <Choice
                label="รูปแบบการแสดงผลหน้าการจอง"
                value={v.bookingLayout}
                onChange={set('bookingLayout')}
                options={[
                  { value: 'default', label: 'ค่าเริ่มต้น' },
                  { value: 'tablet', label: 'เมนูสำหรับแท็บเลต' },
                ]}
              />

              <Choice
                label="แสดงผล Custom ID ของลูกค้า"
                value={v.showCustomId}
                onChange={set('showCustomId')}
                options={[{ value: false, label: 'ไม่แสดง' }, { value: true, label: 'แสดง' }]}
              />

              <button
                className="btn-solid"
                style={{ marginTop: 16 }}
                disabled={!form || saving}
                onClick={save}
              >
                {saving ? 'กำลังบันทึก…' : 'แก้ไข'}
              </button>
            </div>
          );
        }}
      </Panel>
    </SettingsPage>
  );
}

/* ── ข้อมูลร้าน ───────────────────────────────────────────────────────── */
